import { iconPath, waitForGlobalVariable, cartMapping } from '@ui/helpers'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  genSid,
  useCheckoutService,
  useEventFbId,
  useLocalBuyNowOrder,
  useLocalIsPuchase,
  useLocalOrder,
  useLocalOrderId,
  useLocalSessionId,
  useLocalShippingAddress,
  useOrderService,
  useRefDep,
  useTrackingService
} from '@libs/client'
import { CustomerAddress, CustomerAddressField } from '@libs/client/order/services'
import {
  useAnalytics,
  useFbp,
  usePinterest,
  useRedirect,
  useSetting,
  useSnapChat,
  useTtq
} from '@ui/hooks'
import { useRouter } from 'next/router'
import css from 'styled-jsx/css'
import getConfig from 'next/config'
import { logger } from '@ui/analytics'

const config = getConfig().publicRuntimeConfig
import { useNotiContext } from '../Notification'
import { useGtag } from '@ui/hooks/useGtag'
import { useAxon } from '@ui/hooks/useAxon'
import { useAmazonPayCleanup } from '@ui/hooks/useAmazonPayCleanup'

function useAmazonPayScript() {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  useEffect(() => {
    const amazonPayScript = document.createElement('script')
    amazonPayScript.src = 'https://static-na.payments-amazon.com/checkout.js'
    amazonPayScript.async = true
    amazonPayScript.onload = () => {
      setIsScriptLoaded(true)
    }
    document.head.appendChild(amazonPayScript)
  }, [])

  return isScriptLoaded
}

interface IAmazonPayButtonProps {
  setLoadingState: (loading: boolean) => void
  isBuyNow: boolean
  isAbandonedCheckout?: boolean
}

export default function AmazonPayButton({
  setLoadingState,
  isBuyNow,
  isAbandonedCheckout = false
}: IAmazonPayButtonProps) {
  const isScriptLoaded = useAmazonPayScript()
  const shippingAddressRef = useRef<AmazonAddress | null>(null)
  const redirect = useRedirect()
  const router = useRouter()
  const buyerRef = useRef<Buyer | null>(null)
  const { notiDispatch } = useNotiContext()
  const [sellpageSetting] = useSetting('sellpage')
  const { initAmazonPay, changeAddressAmazonPay, finalizeCheckoutAmazonPay } = useCheckoutService({
    cartMapping: (cart) => cartMapping(cart, sellpageSetting?.variants?.items ?? {})
  })
  const [localBuyNowOrder] = useLocalBuyNowOrder()
  const localBuyNowOrderRef = useRefDep(localBuyNowOrder)
  const [localOrder] = useLocalOrder()
  const localOrderRef = useRefDep(localOrder)
  const { initItems, updateShipping } = useOrderService()
  const [localOrderId] = useLocalOrderId()
  const localOrderIdRef = useRefDep(localOrderId ?? '')
  const [, setLocalShippingAddress] = useLocalShippingAddress()
  const currentOrder = useRef<Types.Order | null>(null)
  const analytics = useAnalytics()
  const analyticsRef = useRefDep(analytics)
  const [, setLocalSessionId] = useLocalSessionId()
  const [, setEventFbId] = useEventFbId()
  const [, setLocalIsPuchase] = useLocalIsPuchase()
  const [localLoading, setLocalLoading] = useState(true)
  useAmazonPayCleanup()
  const {
    fbpTrackInitiateCheckout,
    fbpTrackAddPaymentInfo,
    fbpTrackPurchase,
    fbpInitAdvanceMatching
  } = useFbp()
  const { pinterestTrackPurchase } = usePinterest()
  const {
    snapchatTrackInitiateCheckout,
    snapchatTrackAddPaymentInfo,
    snapchatTrackPurchase
  } = useSnapChat()
  const {
    ttqTrackInitiateCheckout,
    ttqTrackAddBilling,
    ttqTrackPurchase,
    ttqTrackIdentify
  } = useTtq()
  const {
    gtagTrackBeginCheckout,
    gtagTrackAddPaymentInfo,
    gtagTrackAddShippingInfo,
    gtagTrackPurchase,
    gtagTrackConversionPurchase,
    gtagEnhanceConversion
  } = useGtag()
  const { axonTrackBeginCheckout, axonTrackPurchase } = useAxon()
  const { trackingInitCheckoutAmazonPay, trackingCancelAmazonCheckout } = useTrackingService()

  function convertToCustomerAddress(address: AmazonAddress, buyer?: Buyer): CustomerAddress {
    return {
      address1: address.addressLine1,
      address2: address.addressLine2,
      city: address.city,
      country: address.countryCode,
      country_code: address.countryCode,
      postal_code: address.postalCode,
      state: address.stateOrRegion,
      first_name: address.name.split(' ')[0],
      last_name: address.name.split(' ')[1],
      email: buyer?.email,
      phone: buyer?.phoneNumber
    }
  }

  useEffect(() => {
    if (!isScriptLoaded) return
    waitForGlobalVariable('amazon').then(async (amazon) => {
      setLocalLoading(false)
      if (amazon && amazon.Pay !== null) {
        const checkoutSessionConfig: CheckoutSessionPayload = {
          storeId: config?.azStoreId || '',
          scopes: ['name', 'email', 'phoneNumber', 'billingAddress'],
          paymentDetails: {
            paymentIntent: 'AuthorizeWithCapture'
          }
        }
        const amazonPayButtonConfig: AmazonPayButtonConfig = {
          merchantId: config?.azMerchantId || '',
          publicKeyId: config?.azPublicKey || '',
          ledgerCurrency: 'USD',
          checkoutLanguage: 'en_US',
          productType: 'PayAndShip',
          buttonColor: 'DarkGray',
          sandbox: config?.env === 'local' || config?.env === 'dev',
          placement: 'Cart',
          checkoutSessionConfig,
          onInitCheckout: async (event) => {
            const shippingAddress = convertToCustomerAddress(event.shippingAddress, event.buyer)
            buyerRef.current = event.buyer
            const currentCartItems =
              (isBuyNow ? localBuyNowOrderRef.current?.items : localOrderRef.current?.items) || []
            if (currentCartItems.length < 1) {
              throw {
                status: 'error',
                reasonCode: 'No items in cart'
              } as AmazonPayHandlerError
            }
            const cartInitItems = await initItems(currentCartItems, isBuyNow ?? false, 'AMAZON')
            if (cartInitItems?.id) {
              localOrderIdRef.current = cartInitItems.id

              setLocalShippingAddress(shippingAddress)
              ;(Object.keys(shippingAddress) as Array<keyof CustomerAddress>).forEach((key) => {
                updateShipping(key as CustomerAddressField, shippingAddress[key])
              })
            }

            const cartDetails = await initAmazonPay(shippingAddress)
            shippingAddressRef.current = event.shippingAddress
            const { items, items_amount } = cartInitItems
            if (!isBuyNow) {
              fbpTrackInitiateCheckout(items, items_amount)
              snapchatTrackInitiateCheckout(items, items_amount)
              ttqTrackInitiateCheckout(items, items_amount)
              gtagTrackBeginCheckout(items, items_amount)
              axonTrackBeginCheckout(items, items_amount)
              logger.logProductEvent('checkout_azp_cart')
              if (router.query?.afs) logger.logProductEvent('aftersale_checkout_azp_cart')
            } else {
              logger.logProductEvent('checkout_azp_buy_now')
              if (router.query?.afs) logger.logProductEvent('aftersale_checkout_azp_buy_now')
            }
            currentOrder.current = cartInitItems

            if (!cartDetails) {
              notiDispatch({
                type: 'REMOVE_ALL_AND_ADD',
                payload: {
                  content:
                    (cartDetails as AmazonPayHandlerError).reasonCode ||
                    'There was a problem when processing your payment. Please try again later.',
                  type: 'is-danger'
                }
              })
              return {
                status: 'error',
                reasonCode: 'unknownError'
              }
            }

            return cartDetails
          },
          onShippingAddressSelection: async (event) => {
            const shippingAddress = convertToCustomerAddress(
              event.shippingAddress,
              buyerRef.current || undefined
            )
            const result = await changeAddressAmazonPay(shippingAddress)
            if (!result) {
              notiDispatch({
                type: 'REMOVE_ALL_AND_ADD',
                payload: {
                  content:
                    (result as AmazonPayHandlerError).reasonCode ||
                    'There was a problem when processing your payment. Please try again later.',
                  type: 'is-danger'
                }
              })
              return {
                status: 'error',
                reasonCode: 'unknownError'
              }
            }
            shippingAddressRef.current = event.shippingAddress
            return result
          },
          onCompleteCheckout: async (event) => {
            setLoadingState(true)
            if (currentOrder.current) {
              const { items, items_amount } = currentOrder.current
              fbpTrackAddPaymentInfo(items, items_amount)
              snapchatTrackAddPaymentInfo(items, items_amount)
              ttqTrackAddBilling(items, items_amount)
              gtagTrackAddPaymentInfo(items, items_amount, 'Paypal')
              gtagTrackAddShippingInfo(items, items_amount)
              logger.logProductEvent('paid_azp')
            }
            if (shippingAddressRef.current) {
              const shippingAddress = convertToCustomerAddress(
                shippingAddressRef.current,
                buyerRef.current || undefined
              )
              const billingAddress = convertToCustomerAddress(event.billingAddress)
              const result = await finalizeCheckoutAmazonPay(
                event.amazonCheckoutSessionId,
                shippingAddress,
                billingAddress,
                event?.paymentDescriptor ?? ''
              )
              if ((result as AmazonPayHandlerError).status === 'error') {
                notiDispatch({
                  type: 'REMOVE_ALL_AND_ADD',
                  payload: {
                    content:
                      (result as AmazonPayHandlerError).reasonCode ||
                      'There was a problem when processing your payment. Please try again later.',
                    type: 'is-danger'
                  }
                })
                setLoadingState(false)
                return
              }
              const { items, amount, shipping } = result as Types.Order
              fbpTrackPurchase(items, amount)
              fbpInitAdvanceMatching(shipping)
              pinterestTrackPurchase(amount)
              snapchatTrackPurchase(items, amount)
              ttqTrackIdentify(shipping)
              ttqTrackPurchase(items, amount)
              gtagTrackPurchase(result as Types.Order)
              axonTrackPurchase(result as Types.Order)
              gtagTrackConversionPurchase(
                `AW-${analyticsRef?.current?.google_conversion_id || ''}/${
                  analyticsRef?.current?.google_conversion_label || ''
                }`,
                result as Types.Order
              )
              gtagEnhanceConversion(result as Types.Order)
              setLocalSessionId(genSid())
              setEventFbId(genSid())
              setLocalIsPuchase(true)
              if (!isBuyNow) {
                logger.logProductEvent('payment_success_azp_cart')
              } else {
                logger.logProductEvent('payment_success_azp_buy_now')
              }
              logger.logProductEvent('payment_success')
              if (router.query?.afs || '') {
                logger.logProductEvent('aftersale_payment_success_azp')
                logger.logProductEvent('aftersale_payment_success')
              }
              const query = router.query
              notiDispatch({
                type: 'REMOVE_ALL_AND_ADD',
                payload: {
                  content: 'Your order has been placed successfully.',
                  type: 'is-success'
                }
              })
              redirect('/thankyou', {
                query: { ...query, afs: (result as SfTypes.Order)?.id },
                keepQuery: false
              })
              setLoadingState(false)
              return
            }
            setLoadingState(false)
            notiDispatch({
              type: 'REMOVE_ALL_AND_ADD',
              payload: {
                content: 'Shipping address is not available.',
                type: 'is-danger'
              }
            })
          },
          onCancel: (error) => {
            console.error('User cancelled checkout', error)
            trackingCancelAmazonCheckout(isBuyNow, isAbandonedCheckout)
            if (!isAbandonedCheckout) {
              logger.logProductEvent('amazon_pay_cancel')
            }
          }
        }

        amazon.Pay.renderJSButton('#amazon-pay-button', amazonPayButtonConfig)
      }
    })
  }, [isScriptLoaded, isBuyNow])

  const handleAmazonPayClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      if (!isScriptLoaded) return
      trackingInitCheckoutAmazonPay(isBuyNow)
      const amazonPayButton = document.getElementById('amazon-pay-button')
      if (amazonPayButton) amazonPayButton.click()
    },
    [isScriptLoaded]
  )

  return (
    <>
      <style jsx>{style}</style>
      <div className={'amazon-pay-button-container'}>
        <div className={'amazon-pay-button'} id="amazon-pay-button"></div>
        {localLoading ? (
          <button className="amazon-pay-button-display button is-loading is-warning" />
        ) : (
          <button className={'amazon-pay-button-display'} onClick={handleAmazonPayClick}>
            <img src={iconPath('amazon_button.svg')} alt="card" className="mt-2" />
          </button>
        )}
      </div>
    </>
  )
}

const style = css`
  .amazon-pay-button-container {
    flex: 1;
  }

  .amazon-pay-button {
    display: none;
  }

  .amazon-pay-button-display {
    width: 100%;
    background-color: #fad676;
    border: none;
    height: 100%;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`
